import {
  Allergen,
  CatMedicalCondition,
  type CatActivityLevel,
  type CatBodyType,
} from "@/primary-models";
import {
  AGE_TYPE_KEYS,
  BINARY_GENDER_KEYS,
  BREED_KEYS,
  GENDER_KEYS,
} from "./cat-calculations.constants";

export type Breed = typeof BREED_KEYS[number];
export type AgeType = typeof AGE_TYPE_KEYS[number];
export type Gender = typeof GENDER_KEYS[number];
export type BinaryGender = typeof BINARY_GENDER_KEYS[number];

export interface IEstimatedWeights {
  lower: number;
  estimated: number;
  upper: number;
}

export type AdultWeightProfile = Record<BodyType, IEstimatedWeights>;

/**
 * Identifies which adult weight profile a cat grows towards. Both values are optional because
 * several callers hold cats with unknown breed/sex; those fall back to an ungendered moggie.
 */
export interface ICatProfile {
  breed?: Breed | null;
  gender?: Gender | null;
}

export interface IBreed {
  value: Breed;
  text: string;
  maleWeights: AdultWeightProfile;
  femaleWeights: AdultWeightProfile;
  isMoggie: boolean;
  isPedigree: boolean;
  isLongHaired?: boolean;
}

export enum ECatResides {
  INDOOR = "INDOOR",
  OUTDOOR = "OUTDOOR",
}

export type LifeStyle = CatActivityLevel;
export type BodyType = CatBodyType;
export type PouchSize = "A" | "B" | "C" | "D" | "E" | "F";
export type Portion = 0.75 | 1 | 1.25 | 1.5 | 1.75 | 2;

export function isPouchSize(string: string): string is PouchSize {
  const pouchSizes: PouchSize[] = [ "A", "B", "C", "D", "E", "F" ];
  return pouchSizes.includes(string as PouchSize);
}

type TBodyTypeIcon = "catBodyVeryThin" | "catBodyThin" | "catBodyJustRight" | "catBodyOverweight" | "catBodyVeryOverweight";

export interface IBodyType {
  name: string;
  icon: TBodyTypeIcon;
}

export type BodyTypeCalorieMultiplier = Record<BodyType, number>;
export type LifeStyleCalorieMultiplier = Record<LifeStyle, number>;
export type MealPlanCalories = Record<PouchSize, number>;
export type RetailTrayPortions = Record<number, Portion>;

export interface IMealPlan {
  size: PouchSize;
  calories: number;
}

export interface IRetailTrayPortion {
  portion: Portion;
  calories: number;
}

export interface ICatAge {
  type?: AgeType;
  mode?: "months" | "weeks" | "years";
  years?: number;
  months?: number;
  weeks?: number;
}

export interface INumeratorDenominator {
  numerator: number;
  denominator: number;
}
export interface IFraction {
  wholeNumber?: number;
  fraction?: INumeratorDenominator;
}

export type TDecimalRounding = "UP" | "DOWN";

export interface IMedicalCondition {
  value: CatMedicalCondition,
  title: string,
  aliases: string[],
}

export interface IAllergy {
  value: Allergen,
  title: string,
  aliases: string[],
}
